/**
 * Inline spinner utility — writes animated progress to stderr.
 * No external dependencies (no ora). Uses ANSI escape codes + setInterval.
 */

const FRAMES = ["\u28CB", "\u28D9", "\u28F9", "\u28F8", "\u28FC", "\u28F4", "\u28E6", "\u28E7", "\u28C7", "\u28CF"];
const INTERVAL_MS = 80;

/**
 * Wraps an async operation with an animated spinner on stderr.
 *
 * - If stderr is not a TTY, runs silently (no animation).
 * - On success: clears line, prints checkmark with elapsed time.
 * - On error: clears line, prints cross, re-throws.
 */
export async function withSpinner<T>(text: string, fn: () => Promise<T>): Promise<T> {
  const start = Date.now();
  const isTTY = process.stderr.isTTY;

  if (!isTTY) {
    return fn();
  }

  let frameIndex = 0;
  const timer = setInterval(() => {
    const frame = FRAMES[frameIndex % FRAMES.length];
    process.stderr.write(`\r\x1b[K${frame} ${text}`);
    frameIndex++;
  }, INTERVAL_MS);

  try {
    const result = await fn();
    clearInterval(timer);
    const elapsed = ((Date.now() - start) / 1000).toFixed(1);
    process.stderr.write(`\r\x1b[K\x1b[32m\u2713\x1b[0m ${text} (${elapsed}s)\n`);
    return result;
  } catch (error) {
    clearInterval(timer);
    process.stderr.write(`\r\x1b[K\x1b[31m\u2717\x1b[0m ${text}\n`);
    throw error;
  }
}
